import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useFormData, useFormActions, useIdentityDocumentsStatus, useFormMode, useFormStore } from '@hooks/useFormStore'
import { Button } from '@components/UI/Button'
import { Input } from '@components/UI/Input'
import { DropZone } from '@components/Upload/DropZone'
import { CameraModal } from '@components/Camera/CameraModal'
import { uploadApi, identityDocumentsApi, submissionsApi } from '@/services/api'
import { isIdentityDocumentRequired, isIdentityAttachmentRequired } from '@/types'
import type { FormData, TipoDocumento } from '@/types'
import styles from './FormSection.module.css'

// NOTA: la logica di OCR / estrazione automatica dei dati documento e' stata
// rimossa. L'operatore inserisce manualmente i dati del documento d'identita';
// il backend salva solo le immagini come allegati al PDF firmato finale.

/**
 * Converte una data da formato italiano (GG/MM/AAAA) o ISO (AAAA-MM-GG) a Date
 */
function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null
  
  let giorno: number, mese: number, anno: number
  
  // Supporta formato GG/MM/AAAA
  if (dateStr.includes('/')) {
    const parti = dateStr.split('/')
    if (parti.length !== 3) return null
    giorno = parseInt(parti[0], 10)
    mese = parseInt(parti[1], 10) - 1 // Mesi 0-indexed
    anno = parseInt(parti[2], 10)
  } else if (dateStr.includes('-')) {
    // Supporta formato AAAA-MM-GG
    const parti = dateStr.split('-')
    if (parti.length !== 3) return null
    anno = parseInt(parti[0], 10)
    mese = parseInt(parti[1], 10) - 1
    giorno = parseInt(parti[2], 10)
  } else {
    return null
  }
  
  if (isNaN(giorno) || isNaN(mese) || isNaN(anno)) return null
  
  const date = new Date(anno, mese, giorno)
  // Verifica che la data sia valida
  if (date.getFullYear() !== anno || date.getMonth() !== mese || date.getDate() !== giorno) {
    return null
  }
  
  return date
}

type DocumentoFields = Pick<FormData,
  'tipo_documento' | 'numero_documento' | 'data_emissione_documento' | 'data_scadenza_documento' | 'ente_rilascio'
>

interface DocumentTypeOption {
  value: TipoDocumento
  label: string
  icon: string
  entePrefix: string
}

const DOCUMENT_TYPES: DocumentTypeOption[] = [
  {
    value: 'carta_identita',
    label: "Carta d'Identità",
    icon: 'bi-person-badge',
    entePrefix: 'Comune'
  },
  {
    value: 'patente',
    label: 'Patente',
    icon: 'bi-car-front',
    entePrefix: 'M.C.T.C.'
  },
  {
    value: 'passaporto',
    label: 'Passaporto',
    icon: 'bi-globe-europe-africa',
    entePrefix: 'Questura'
  }
]

interface DocumentSideUpload {
  uploaded: boolean
  fileName?: string
  isUploading: boolean
  error?: string
}

export function DocumentoSection() {
  const formData = useFormData()
  const { setData, nextStep, prevStep, setIdentityDocumentsStatus } = useFormActions()
  const identityDocsStatus = useIdentityDocumentsStatus()
  const mode = useFormMode()
  const isOperatorMode = mode === 'operator'
  // submissionId dello store: se gia' esiste persistiamo il flag
  // "non ho il documento del cliente" subito sul backend per evitare
  // falsi negativi al riepilogo in caso di refresh/navigazione.
  const submissionId = useFormStore((state) => state.submissionId)
  const [selectedType, setSelectedType] = useState<TipoDocumento | null>(formData.tipo_documento)
  const [showCameraModal, setShowCameraModal] = useState(false)
  const [cameraTarget, setCameraTarget] = useState<'fronte' | 'retro'>('fronte')
  
  // State per upload separato fronte/retro
  const [fronteUpload, setFronteUpload] = useState<DocumentSideUpload>({
    uploaded: false,
    isUploading: false
  })
  const [retroUpload, setRetroUpload] = useState<DocumentSideUpload>({
    uploaded: false,
    isUploading: false
  })
  
  // Operatore senza documento del cliente: caricamento delegato al cliente
  // (gli estremi del documento restano comunque obbligatori).
  const clientWillUpload = isOperatorMode && !!formData.client_must_upload_identity

  const handleToggleClientWillUpload = (next: boolean) => {
    setData({ client_must_upload_identity: next })
    // Persistiamo subito sul backend se la submission esiste, cosi' il flag
    // sopravvive a refresh / navigazione "Indietro" senza ricadere sul default 0.
    if (submissionId) {
      submissionsApi
        .update(submissionId, { client_must_upload_identity: next })
        .catch((err) => {
          console.error('Persist client_must_upload_identity failed:', err)
        })
    }
  }

  const documentRequired = isIdentityDocumentRequired(formData, isOperatorMode ? 'operator' : 'self')
  const attachmentRequired = isIdentityAttachmentRequired(formData, isOperatorMode ? 'operator' : 'self')
  // Pratica di solo switch dal 21/09/2026: gli estremi servono, la copia no.
  // Nascondiamo gli allegati solo in questo caso, per non togliere l'upload
  // facoltativo dove non c'entra l'esenzione (es. business self-service).
  const attachmentExempted = documentRequired && !attachmentRequired
  const hasRequiredDocs = (fronteUpload.uploaded && retroUpload.uploaded) || (identityDocsStatus?.has_required_documents ?? false)
  const attachmentsSatisfied = clientWillUpload || hasRequiredDocs

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<DocumentoFields>({
    mode: 'onBlur',
    defaultValues: {
      tipo_documento: formData.tipo_documento,
      numero_documento: formData.numero_documento,
      data_emissione_documento: formData.data_emissione_documento,
      data_scadenza_documento: formData.data_scadenza_documento,
      ente_rilascio: formData.ente_rilascio
    }
  })

  // Fetch identity documents status on mount.
  // Passiamo il submissionId (se presente) cosi' il backend include anche
  // i documenti gia' legati alla pratica corrente, ma esclude quelli di
  // pratiche precedenti completate nella stessa sessione browser.
  const refreshIdentityDocsStatus = useCallback(async () => {
    try {
      const status = await identityDocumentsApi.getStatus(submissionId)
      setIdentityDocumentsStatus(status)
      
      const documents = status.documents || []
      const hasFronte = documents.some((doc: { image_type?: string }) => doc.image_type === 'fronte')
      const hasRetro = documents.some((doc: { image_type?: string }) => doc.image_type === 'retro')
      const fronteDoc = documents.find((doc: { image_type?: string }) => doc.image_type === 'fronte')
      const retroDoc = documents.find((doc: { image_type?: string }) => doc.image_type === 'retro')
      
      if (hasFronte) {
        setFronteUpload({
          uploaded: true,
          fileName: (fronteDoc as { original_filename?: string })?.original_filename || 'Documento fronte',
          isUploading: false
        })
      }
      if (hasRetro) {
        setRetroUpload({
          uploaded: true,
          fileName: (retroDoc as { original_filename?: string })?.original_filename || 'Documento retro',
          isUploading: false
        })
      }
    } catch (error) {
      console.error('Error fetching identity documents status:', error)
    }
  }, [setIdentityDocumentsStatus, submissionId])

  useEffect(() => {
    refreshIdentityDocsStatus()
  }, [refreshIdentityDocsStatus])

  // Auto-suggest ente rilascio when document type changes
  useEffect(() => {
    if (selectedType) {
      const docType = DOCUMENT_TYPES.find(d => d.value === selectedType)
      if (docType) {
        setValue('ente_rilascio', docType.entePrefix)
      }
    }
  }, [selectedType, setValue])

  const handleDocumentTypeSelect = (type: TipoDocumento) => {
    setSelectedType(type)
    setValue('tipo_documento', type)
  }

  // Handler per upload FRONTE documento
  const handleFronteUpload = async (files: File[]) => {
    if (files.length === 0) return

    setFronteUpload(prev => ({ ...prev, isUploading: true, error: undefined }))

    try {
      const file = files[0]
      await uploadApi.uploadIdentity(file, 'fronte', submissionId)
      setFronteUpload({
        uploaded: true,
        fileName: file.name,
        isUploading: false
      })
      refreshIdentityDocsStatus()
    } catch (error) {
      setFronteUpload(prev => ({
        ...prev,
        isUploading: false,
        error: error instanceof Error ? error.message : 'Errore caricamento fronte'
      }))
    }
  }

  // Handler per upload RETRO documento
  const handleRetroUpload = async (files: File[]) => {
    if (files.length === 0) return

    setRetroUpload(prev => ({ ...prev, isUploading: true, error: undefined }))

    try {
      const file = files[0]
      await uploadApi.uploadIdentity(file, 'retro', submissionId)
      setRetroUpload({
        uploaded: true,
        fileName: file.name,
        isUploading: false
      })
      refreshIdentityDocsStatus()
    } catch (error) {
      setRetroUpload(prev => ({
        ...prev,
        isUploading: false,
        error: error instanceof Error ? error.message : 'Errore caricamento retro'
      }))
    }
  }

  // Handler per cattura foto con camera
  const handleCameraCapture = async (blobs: Blob[]) => {
    setShowCameraModal(false)
    if (blobs.length === 0) return

    const target = cameraTarget
    const setUpload = target === 'fronte' ? setFronteUpload : setRetroUpload

    setUpload(prev => ({ ...prev, isUploading: true, error: undefined }))

    try {
      await uploadApi.uploadIdentityBlob(blobs[0], `documento-${target}.jpg`, target, submissionId)
      setUpload({
        uploaded: true,
        fileName: `Foto ${target}`,
        isUploading: false
      })
      refreshIdentityDocsStatus()
    } catch (error) {
      setUpload(prev => ({
        ...prev,
        isUploading: false,
        error: error instanceof Error ? error.message : `Errore caricamento ${target}`
      }))
    }
  }

  const openCameraFor = (side: 'fronte' | 'retro') => {
    setCameraTarget(side)
    setShowCameraModal(true)
  }

  // Handler per rimuovere documenti
  const handleRemoveDocument = async () => {
    try {
      await identityDocumentsApi.deleteAll(submissionId)
      setFronteUpload({ uploaded: false, isUploading: false })
      setRetroUpload({ uploaded: false, isUploading: false })
      await refreshIdentityDocsStatus()
    } catch (error) {
      console.error('Error removing documents:', error)
    }
  }

  const onSubmit = (data: DocumentoFields) => {
    setData({
      ...data,
      tipo_documento: selectedType
    })
    nextStep()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <i className="bi bi-person-vcard-fill" />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>
            Documento d'Identità
            {documentRequired && (
              <span className={styles.requiredBadge}>
                <i className="bi bi-asterisk" /> Obbligatorio
              </span>
            )}
          </h2>
          <p className={styles.sectionDescription}>
            Inserisci i dati del documento e allega fronte e retro
          </p>
        </div>
      </div>


      {/* Document Type Selection */}
      <div className={styles.documentTypeSection}>
        <label className={styles.fieldLabel}>Tipo di documento</label>
        <div className={styles.documentTypeGrid}>
          {DOCUMENT_TYPES.map((docType) => (
            <button
              key={docType.value}
              type="button"
              className={`${styles.documentTypeCard} ${selectedType === docType.value ? styles.documentTypeCardSelected : ''}`}
              onClick={() => handleDocumentTypeSelect(docType.value)}
            >
              <div className={styles.documentTypeIcon}>
                <i className={`bi ${docType.icon}`} />
              </div>
              <span className={styles.documentTypeLabel}>{docType.label}</span>
              {selectedType === docType.value && (
                <div className={styles.documentTypeCheck}>
                  <i className="bi bi-check-circle-fill" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Document Fields - Always visible */}
      <div className={styles.divider}>
        <span>Dati del documento</span>
      </div>

      <div className={styles.formGrid}>
        <Input
          label="Numero Documento"
          icon="card-text"
          required
          placeholder="AB1234567"
          error={errors.numero_documento?.message}
          {...register('numero_documento', { 
            required: 'Numero documento obbligatorio'
          })}
        />
        
        <Input
          label="Data di Emissione"
          icon="calendar-check"
          type="text"
          required
          placeholder="GG/MM/AAAA"
          error={errors.data_emissione_documento?.message}
          {...register('data_emissione_documento', { 
            required: 'Data di emissione obbligatoria',
            pattern: {
              value: /^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/,
              message: 'Formato data non valido (GG/MM/AAAA)'
            },
            validate: (value) => {
              if (!value) return true
              const date = parseDate(value)
              if (!date) return true // Pattern già gestito
              const today = new Date()
              today.setHours(0, 0, 0, 0)
              if (date > today) return 'La data di emissione non può essere nel futuro'
              return true
            }
          })}
        />
        
        <Input
          label="Data di Scadenza"
          icon="calendar-x"
          type="text"
          required
          placeholder="GG/MM/AAAA"
          error={errors.data_scadenza_documento?.message}
          {...register('data_scadenza_documento', { 
            required: 'Data di scadenza obbligatoria',
            pattern: {
              value: /^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/,
              message: 'Formato data non valido (GG/MM/AAAA)'
            },
            validate: (value) => {
              if (!value) return true
              const date = parseDate(value)
              if (!date) return true // Pattern già gestito
              const today = new Date()
              today.setHours(0, 0, 0, 0)
              if (date < today) return 'Il documento risulta scaduto. Verifica la data di scadenza'
              return true
            }
          })}
        />
        
        <Input
          label="Ente di Rilascio"
          icon="building"
          required
          placeholder="Comune di Roma"
          error={errors.ente_rilascio?.message}
          {...register('ente_rilascio', { 
            required: 'Ente di rilascio obbligatorio'
          })}
        />
      </div>

      {/* Allegati: sulle pratiche di solo switch non vanno ne' richiesti ne'
          inviati nel plico, quindi la sezione non viene mostrata affatto. */}
      {attachmentExempted ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.875rem 1rem',
            marginTop: '1.5rem',
            borderRadius: '0.5rem',
            border: '1px solid var(--color-gray-200)',
            background: 'var(--color-gray-50)',
            color: 'var(--color-gray-700)',
            fontSize: '0.875rem',
          }}
        >
          <i className="bi bi-info-circle" style={{ fontSize: '1.1rem' }} />
          <span>
            Per il cambio fornitore la copia del documento non va allegata:
            bastano i dati inseriti qui sopra.
          </span>
        </div>
      ) : (
      <>
      <div className={styles.divider}>
        <span>Allegati documento</span>
      </div>

      {clientWillUpload && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.875rem 1rem',
            marginBottom: '1rem',
            borderRadius: '0.5rem',
            border: '1px solid var(--color-primary-200, #f5d0e3)',
            background: 'var(--color-primary-50, #fdf2f8)',
            color: 'var(--color-primary-700, #9d174d)',
            fontSize: '0.875rem',
          }}
        >
          <i className="bi bi-cloud-upload-fill" style={{ fontSize: '1.1rem' }} />
          <span>
            Caricamento delegato al cliente: fronte e retro saranno richiesti durante la firma online.
          </span>
        </div>
      )}

      <div
        className={styles.documentAttachments}
        style={clientWillUpload ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        aria-disabled={clientWillUpload}
      >
        {/* FRONTE */}
        <div className={`${styles.documentAttachmentCard} ${fronteUpload.uploaded ? styles.documentAttachmentUploaded : ''}`}>
          <div className={styles.documentAttachmentHeader}>
            <i className="bi bi-card-image" />
            <span>FRONTE</span>
            {fronteUpload.uploaded && <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-success-500)', marginLeft: 'auto' }} />}
          </div>
          
          {fronteUpload.uploaded ? (
            <div className={styles.documentAttachmentPreview}>
              <i className="bi bi-file-earmark-check" />
              <span>{fronteUpload.fileName}</span>
              <button type="button" onClick={handleRemoveDocument} className={styles.removeAttachment}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ) : (
            <div className={styles.documentAttachmentActions}>
              <DropZone
                onFilesSelected={handleFronteUpload}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg']
                }}
                maxSize={20 * 1024 * 1024}
                disabled={fronteUpload.isUploading}
                multiple={false}
                label="Carica file"
                dragLabel="Rilascia"
                compact
              />
              <button 
                type="button" 
                className={styles.cameraButton}
                onClick={() => openCameraFor('fronte')}
                disabled={fronteUpload.isUploading}
              >
                <i className="bi bi-camera" />
                <span>Scatta foto</span>
              </button>
            </div>
          )}
          
          {fronteUpload.isUploading && (
            <div className={styles.uploadingIndicator}>
              <i className="bi bi-hourglass-split" /> Caricamento...
            </div>
          )}
          {fronteUpload.error && (
            <div className={styles.attachmentError}>{fronteUpload.error}</div>
          )}
        </div>

        {/* RETRO */}
        <div className={`${styles.documentAttachmentCard} ${retroUpload.uploaded ? styles.documentAttachmentUploaded : ''}`}>
          <div className={styles.documentAttachmentHeader}>
            <i className="bi bi-card-image" />
            <span>RETRO</span>
            {retroUpload.uploaded && <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-success-500)', marginLeft: 'auto' }} />}
          </div>
          
          {retroUpload.uploaded ? (
            <div className={styles.documentAttachmentPreview}>
              <i className="bi bi-file-earmark-check" />
              <span>{retroUpload.fileName}</span>
              <button type="button" onClick={handleRemoveDocument} className={styles.removeAttachment}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ) : (
            <div className={styles.documentAttachmentActions}>
              <DropZone
                onFilesSelected={handleRetroUpload}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg']
                }}
                maxSize={20 * 1024 * 1024}
                disabled={retroUpload.isUploading}
                multiple={false}
                label="Carica file"
                dragLabel="Rilascia"
                compact
              />
              <button 
                type="button" 
                className={styles.cameraButton}
                onClick={() => openCameraFor('retro')}
                disabled={retroUpload.isUploading}
              >
                <i className="bi bi-camera" />
                <span>Scatta foto</span>
              </button>
            </div>
          )}
          
          {retroUpload.isUploading && (
            <div className={styles.uploadingIndicator}>
              <i className="bi bi-hourglass-split" /> Caricamento...
            </div>
          )}
          {retroUpload.error && (
            <div className={styles.attachmentError}>{retroUpload.error}</div>
          )}
        </div>
      </div>

      {/* Success indicator when docs uploaded */}
      {hasRequiredDocs && !clientWillUpload && (
        <div className={styles.successBanner}>
          <i className="bi bi-check-circle-fill" />
          <span>Documento allegato correttamente (fronte e retro)</span>
        </div>
      )}

      {/* Toggle operatore: documento caricato dal cliente in fase di firma */}
      {isOperatorMode && (
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            padding: '0.875rem 1rem',
            marginTop: '1rem',
            borderRadius: '0.5rem',
            border: '1px solid var(--color-gray-200)',
            background: clientWillUpload ? 'var(--color-primary-50, #fdf2f8)' : 'var(--color-gray-50)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={clientWillUpload}
            onChange={(e) => handleToggleClientWillUpload(e.target.checked)}
            style={{ marginTop: '0.25rem' }}
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <strong style={{ color: 'var(--color-gray-900)', fontSize: '0.9rem' }}>
              Non ho il documento del cliente
            </strong>
            <span style={{ color: 'var(--color-gray-600)', fontSize: '0.8125rem', lineHeight: 1.4 }}>
              Sarà il cliente a caricare fronte e retro durante la firma online.
              Gli estremi del documento (numero, date, ente di rilascio) restano comunque obbligatori.
            </span>
          </span>
        </label>
      )}
      </>
      )}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={prevStep}
        >
          Indietro
        </Button>
        
        <div className={styles.actionsRight}>
          <Button
            type="submit"
            icon="arrow-right"
            iconPosition="right"
            disabled={!selectedType || (attachmentRequired && !attachmentsSatisfied)}
          >
            Continua
          </Button>
        </div>
      </div>
      
      {/* Camera Modal */}
      <CameraModal
        isOpen={showCameraModal}
        onClose={() => setShowCameraModal(false)}
        onCapture={handleCameraCapture}
        maxPhotos={1}
      />
    </form>
  )
}
